import moment from "moment";

import { ChartDataPointBase } from "@/Patient/Models/GrowthCharts/ChartData";
import { GrowthChartModel } from "./UIModels/ChartConfigurations/GrowthChartModel";
import { PlotType } from "./GrowthChartsPlotSelection";
import { PatientChartDataTableProps, PatientChartTable } from "./PatientChartTable";

interface GrowthChartsDataTableProps extends PatientChartDataTableProps {
    chart: GrowthChartModel;
    shownPlots: PlotType[];
    hightlightRow?: ChartDataPointBase;
    onVitalSignClick: (vitalSignMedicalItemId: string) => void;
}

class GrowthChartsDataTable extends PatientChartTable<GrowthChartsDataTableProps, never> {
    public render(): JSX.Element {
        return (
            <div className="datatable datatable-corrected">
                <div
                    className={"datatable-wrapper " + (this.props.chart.isPlotSelectionSupported ? "chart-selector" : "no-chart-selector")}
                    ref={ref => (this.tableContainer = ref)}
                >
                    {this.renderTable()}
                </div>
            </div>
        );
    }

    protected renderTableHeader(): JSX.Element {
        const isBothPlotShown = this.shouldShowChronologicalPlot() && this.shouldShowCorrectedAgePlot();
        return (
            <thead>
                <tr>
                    <th rowSpan={2} colSpan={2}>{cacheStore.GetString("DateLabel")}</th>
                    <th colSpan={this.props.chart.hasBornPrematureData() && this.props.chart.isPlotSelectionSupported ? 2 : 1} className={this.getXAxisCellClass()}>
                        {this.props.chart.xAxis?.label}
                    </th>
                    <th rowSpan={2} colSpan={2} className={this.hasLongYAxisLabel() ? "wide-cell" : ""}>{this.props.chart.yAxis?.label}</th>
                    <th colSpan={isBothPlotShown ? 2 : 1} className={this.props.chart.isPlotSelectionSupported ? "datatable-dashed-border-bottom" : ""}>%</th>
                </tr>
                {this.props.chart.isPlotSelectionSupported && (
                    <tr>
                        <th className="datatable-dot datatable-dot" />
                        {this.props.chart.hasBornPrematureData() && this.props.chart.isPlotSelectionSupported &&
                            <th className="datatable-dot datatable-dot-blue" />
                        }
                        {this.shouldShowChronologicalPlot() &&
                            <th data-cy={`growthCharts-label-${this.props.chart.id}-chronological`} className="datatable-dot datatable-dot" />
                        }
                        {this.shouldShowCorrectedAgePlot() &&
                            <th data-cy={`growthCharts-label-${this.props.chart.id}-corrected`} className="datatable-dot datatable-dot-blue" />
                        }
                    </tr>
                )}
            </thead>
        );
    }

    protected renderTableBody(): JSX.Element {
        const chartData = this.props.chart.data;

        if (!chartData?.length)
            return <tbody />;

        const showChronologicalPlot = this.shouldShowChronologicalPlot();
        const showCorrectedAgePlot = this.shouldShowCorrectedAgePlot();

        return (
            <tbody>
                {chartData.map(datarow => {
                    const date = moment(datarow.date);
                    return (
                        <tr
                            key={datarow.id}
                            ref={element => this.tableRows[datarow.id] = element}
                            className={this.getRowClass(datarow.id)}
                            onClick={() => this.props.onVitalSignClick(datarow.medicalItemId)}
                        >
                            <td colSpan={2}>{moment(date).format("YYYY-MM-DD")}</td>
                            <td>{datarow.chronologicalAge?.toFixed(1) ?? ""}</td>
                            {this.props.chart.hasBornPrematureData() && this.props.chart.isPlotSelectionSupported &&
                                <td>{datarow.correctedAge?.toFixed(1) ?? ""}</td>
                            }
                            <td colSpan={2} className={this.hasLongYAxisLabel() ? "wide-cell" : ""}>{datarow.vitalSignValue}</td>
                            {showChronologicalPlot &&
                                <td>{datarow.chronologicalPercentile ?? ""}</td>
                            }
                            {showCorrectedAgePlot &&
                                <td>{datarow.correctedPercentile ?? ""}</td>
                            }
                        </tr>
                    );
                })}
            </tbody>
        );
    }

    private shouldShowChronologicalPlot(): boolean {
        return !this.props.shownPlots.any()
            || this.props.shownPlots.contains("chronological")
            || !this.props.chart.isPlotSelectionSupported;
    }

    private shouldShowCorrectedAgePlot(): boolean {
        return this.props.chart.hasBornPrematureData() && this.props.chart.isPlotSelectionSupported &&
            (!this.props.shownPlots.any() || this.props.shownPlots.contains("corrected"));
    }

    private hasLongYAxisLabel(): boolean {
        return this.props.chart.yAxis?.label?.length > 20;
    }

    private hasLongXAxisLabel(): boolean {
        return this.props.chart.xAxis?.label?.length > 10;
    }

    private getXAxisCellClass(): string {
        return (this.props.chart.isPlotSelectionSupported ? "datatable-dashed-border-bottom" : "")
            + (this.hasLongXAxisLabel() ? "mid-wide-cell" : "");
    }
}

export { GrowthChartsDataTable };
